import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { trackVehicleRemoval } from '../../../utils/notificationStore';

const VehicleManagement = ({ vehicles }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [nickname, setNickname] = useState('');
  const [removeModal, setRemoveModal] = useState({ isOpen: false, vehicleId: null, vehicleLabel: '' });

  const vehicleTypeOptions = [
    { value: 'electric', label: 'Elektrisk' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'petrol', label: 'Bensin' }
  ];

  const getVehicleIcon = (type) => {
    switch (type) {
      case 'electric':
        return 'Zap';
      case 'hybrid':
        return 'Battery';
      default:
        return 'Car';
    }
  };

  const getVehicleColor = (type) => {
    switch (type) {
      case 'electric':
        return 'var(--color-success)';
      case 'hybrid':
        return 'var(--color-primary)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const handleAddVehicle = () => {
    console.log('Adding vehicle:', { registrationNumber, vehicleType, nickname });
    setRegistrationNumber('');
    setVehicleType('');
    setNickname('');
    setIsAdding(false);
  };

  const handleRemoveVehicle = (id) => {
    console.log('Removing vehicle:', id);
  };

  const handleRemoveRequest = (vehicle) => {
    const label = vehicle?.nickname ? `${vehicle?.nickname} (${vehicle?.registrationNumber})` : vehicle?.registrationNumber;
    setRemoveModal({ isOpen: true, vehicleId: vehicle?.id, vehicleLabel: label });
  };

  const handleRemoveConfirm = () => {
    handleRemoveVehicle(removeModal?.vehicleId);
    trackVehicleRemoval(removeModal?.vehicleLabel);
    setRemoveModal({ isOpen: false, vehicleId: null, vehicleLabel: '' });
  };

  const handleRemoveCancel = () => {
    setRemoveModal({ isOpen: false, vehicleId: null, vehicleLabel: '' });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <ConfirmationModal
        isOpen={removeModal?.isOpen}
        onConfirm={handleRemoveConfirm}
        onCancel={handleRemoveCancel}
        title="Fjern kjøretøy"
        message={`Er du sikker på at du vil fjerne kjøretøyet «${removeModal?.vehicleLabel}» fra flåten din?`}
        consequence="Kjøretøyet vil bli fjernet fra kontoen din og vil ikke lenger motta automatiske rabatter ved bompasseringer."
        confirmLabel="Fjern kjøretøy"
        cancelLabel="Avbryt"
        isDanger={true}
      />
      <div className="flex items-center justify-between">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">
          Dine kjøretøy
        </h3>
        {!isAdding && (
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            onClick={() => setIsAdding(true)}
            style={{ 
              background: 'var(--color-accent)', 
              borderColor: 'var(--color-accent)' 
            }}
          >
            Legg til kjøretøy
          </Button>
        )}
      </div>
      {isAdding && (
        <div className="card">
          <h4 className="text-base md:text-lg font-semibold text-foreground mb-6">
            Legg til nytt kjøretøy
          </h4>
          <div className="space-y-4">
            <Input
              label="Registreringsnummer"
              type="text"
              placeholder="AB12345"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e?.target?.value?.toUpperCase())}
              required
            />

            <Select
              label="Kjøretøytype"
              placeholder="Velg type"
              options={vehicleTypeOptions}
              value={vehicleType}
              onChange={setVehicleType}
              required
            />

            <Input
              label="Kallenavn (valgfritt)"
              type="text"
              placeholder="Min bil"
              value={nickname}
              onChange={(e) => setNickname(e?.target?.value)}
            />

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                variant="default"
                size="default"
                onClick={handleAddVehicle}
                className="flex-1"
                style={{ 
                  background: 'var(--color-accent)', 
                  borderColor: 'var(--color-accent)' 
                }}
              >
                Legg til kjøretøy
              </Button>
              <Button
                variant="outline"
                size="default"
                onClick={() => setIsAdding(false)}
                className="flex-1"
              >
                Avbryt
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {vehicles?.map((vehicle) => (
          <div key={vehicle?.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 md:w-14 md:h-14 rounded flex items-center justify-center"
                  style={{ background: `${getVehicleColor(vehicle?.type)}15` }}
                >
                  <Icon 
                    name={getVehicleIcon(vehicle?.type)} 
                    size={24} 
                    color={getVehicleColor(vehicle?.type)} 
                  />
                </div>
                <div>
                  <h4 className="text-base md:text-lg font-semibold text-foreground">
                    {vehicle?.nickname || vehicle?.registrationNumber}
                  </h4>
                  {vehicle?.nickname && (
                    <p className="text-xs md:text-sm text-muted-foreground font-data">
                      {vehicle?.registrationNumber}
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                iconSize={16}
                onClick={() => handleRemoveRequest(vehicle)}
                aria-label={`Fjern ${vehicle?.registrationNumber}`}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 md:p-4 rounded border-2 border-border bg-muted">
                <span className="text-xs text-muted-foreground font-caption block mb-1">
                  Type
                </span>
                <p className="text-sm md:text-base font-medium text-foreground capitalize">
                  {vehicleTypeOptions?.find(opt => opt?.value === vehicle?.type)?.label}
                </p>
              </div>
              <div className="p-3 md:p-4 rounded border-2 border-border bg-muted">
                <span className="text-xs text-muted-foreground font-caption block mb-1">
                  Rabatt
                </span>
                <p className="text-sm md:text-base font-medium text-foreground">
                  {vehicle?.discount}
                </p>
              </div>
            </div>

            {vehicle?.isDefault && (
              <div 
                className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded border-2"
                style={{ 
                  borderColor: 'var(--color-primary)',
                  background: 'rgba(0, 79, 152, 0.1)'
                }}
              >
                <Icon name="Star" size={14} color="var(--color-primary)" />
                <span 
                  className="text-xs font-medium"
                  style={{ color: 'var(--color-primary)' }}
                >
                  Standard kjøretøy
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VehicleManagement;